/**
 * 일지 차트
 * ---------------------------------------------------------------------------
 * 한 장씩 볼 때는 안 보이던 것이 선으로 그리면 보인다 — 하루만 전력이 두
 * 배인 날, 처리효율이 조금씩 떨어지는 방지시설.
 *
 * 막대와 선
 *   하루의 양(가동시간·사용량)은 막대, 비율과 추이(처리효율·수량)는 선이다.
 *   서버가 정해 보낸다.
 *
 * 빈 날
 *   일지가 없는 날은 점을 찍지 않고 선을 끊는다. 0 으로 이으면 "그날 가동을
 *   안 했다" 로 읽힌다 — 실제로는 기록이 없는 것이다.
 */

import { Card, Col, Empty, Row, Typography } from 'antd';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DashboardChart } from '@eco/shared';

const { Text } = Typography;

/** 계열이 여럿일 때 쓸 색. 서로 잘 갈리는 순서로 둔다. */
const PALETTE = [
  '#1677ff',
  '#0f7b6c',
  '#d46b08',
  '#722ed1',
  '#cf1322',
  '#08979c',
  '#7cb305',
  '#c41d7f',
  '#595959',
];

const CHART_HEIGHT = 240;

export function ChartGrid({ charts }: { charts: DashboardChart[] }) {
  return (
    <Row gutter={[16, 16]}>
      {charts.map((chart) => (
        <Col key={chart.key} xs={24} xl={12}>
          <ChartCard chart={chart} />
        </Col>
      ))}
    </Row>
  );
}

function ChartCard({ chart }: { chart: DashboardChart }) {
  const hasData = chart.series.length > 0 && chart.points.some((p) =>
    chart.series.some((s) => p[s] !== null && p[s] !== undefined),
  );

  return (
    <Card
      size="small"
      title={chart.title}
      extra={chart.unit && <Text type="secondary">단위: {chart.unit}</Text>}
    >
      {!hasData ? (
        <div style={{ height: CHART_HEIGHT, display: 'grid', placeItems: 'center' }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="이 기간에 기록된 값이 없습니다" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          {chart.kind === 'bar' ? (
            <BarChart data={chart.points} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              {axes(chart)}
              {chart.series.map((s, i) => (
                <Bar
                  key={s}
                  dataKey={s}
                  fill={PALETTE[i % PALETTE.length]}
                  maxBarSize={18}
                  // 계열이 하나면 쌓지 않는다. 여럿이면 쌓아서 그날의 합이 보이게 한다.
                  stackId={chart.series.length > 1 ? 'total' : undefined}
                />
              ))}
            </BarChart>
          ) : (
            <LineChart data={chart.points} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              {axes(chart)}
              {chart.series.map((s, i) => (
                <Line
                  key={s}
                  type="monotone"
                  dataKey={s}
                  stroke={PALETTE[i % PALETTE.length]}
                  strokeWidth={2}
                  // 기록이 없는 날은 선을 끊는다. 그래서 띄엄띄엄 쓴 날은 선
                  // 없이 점만 남는다 — 점이 작으면(반지름 2) 차트가 빈 것처럼
                  // 보인다. 실제로 그렇게 "표시가 안 된다" 는 말을 들었다.
                  dot={{ r: 4, strokeWidth: 2, fill: '#fff' }}
                  activeDot={{ r: 6 }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </Card>
  );
}

/** 막대·선 공통의 축·격자·범례. */
function axes(chart: DashboardChart) {
  return [
    <CartesianGrid key="grid" strokeDasharray="3 3" vertical={false} />,
    <XAxis
      key="x"
      dataKey="date"
      tickFormatter={(v: string) => v.slice(5).replace('-', '.')}
      tick={{ fontSize: 11 }}
      minTickGap={16}
    />,
    <YAxis key="y" tick={{ fontSize: 11 }} width={48} />,
    <Tooltip
      key="tip"
      labelFormatter={(v) => String(v)}
      formatter={(value) =>
        value === null || value === undefined ? '-' : `${value}${chart.unit ? ` ${chart.unit}` : ''}`
      }
    />,
    // 계열이 하나면 범례는 제목과 같은 말을 되풀이할 뿐이다.
    chart.series.length > 1 ? <Legend key="legend" wrapperStyle={{ fontSize: 12 }} /> : null,
  ];
}
